import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';
import { CoinflipHistory } from '../interfaces/coinflip-history.interface';
import { FairnessButtonComponent } from '@core/components/fairness-button/fairness-button.component';
import { TableColorCellComponent } from '@core/components/table/table-color-cell/table-color-cell.component';
import { TableDateCellComponent } from '@core/components/table/table-date-cell/table-date-cell.component';
import { TableButtonCellComponent } from '@core/components/table/table-button-cell/table-button-cell.component';
import { capitalizeFirstLetter } from '@core/utils/capitalize-first-letter.util';
import { CoinflipCoinComponent } from '@features/coinflip/coinflip-coin/coinflip-coin.component';
import {
  STATUSES_ALLOW_REFUND,
  STATUSES_ERROR,
} from '../constants/statuses.constants';
import { CoinflipCoin } from '@features/coinflip/types/coins.type';
import {
  Coinflip,
  CoinflipSide,
} from '@old-app/coinflip/interfaces/coinflip.interface';
import { PlayerRowComponent } from '@core/components/player-row/player-row.component';
import { UserHistoryService } from '../services/user-history.service';
import { ModalService } from '@features/modals/services/modal.service';
import { CostComponent } from '@core/components/cost/cost.component';

interface Props {
  userHistoryService: UserHistoryService;
  modalService: ModalService;
}

export const coinflipHistoryTableConfig = ({
  userHistoryService,
  modalService,
}: Props): ColumnDef<CoinflipHistory>[] => [
  {
    accessorFn: (row) => row.gameid,
    id: 'gameid',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'ID',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.value,
    id: 'value',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Amount',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: cell.getValue() as number,
          balanceType: cell.row.original.balancetype ?? 'skins',
          gap: 4,
          color: cell.row.original.winner ? 'var(--green)' : 'var(--red)',
        },
      });
    },
    enableSorting: true,
  },
  {
    id: 'details',
    header: () => 'Details',
    cell: (cell) => {
      const transformData = (data: CoinflipHistory): Coinflip => {
        const host: CoinflipSide = {
          skins: userHistoryService.getSkinsByHistorySkins(
            cell.row.original.host_skins
          ),
          side: data.hostchoice as 1 | 2,
          chance:
            data.winnerid === data.hostid
              ? data.winnerpercent
              : data.loserpercent,
          value: data.hostvalue,
          steamid: data.hostid,
          name: data.hostname,
          avatar: data.hostavatar,
          level: data.hostlevel,
        };

        const part: CoinflipSide = {
          skins: userHistoryService.getSkinsByHistorySkins(
            cell.row.original.part_skins
          ),
          side: data.hostchoice === 1 ? 2 : 1,
          chance:
            data.winnerid === data.partid
              ? data.winnerpercent
              : data.loserpercent,
          value: data.partvalue,
          steamid: data.partid,
          name: data.partname,
          avatar: data.partavatar,
          level: data.partlevel,
        };
        const winner: CoinflipSide =
          data.winnerid === data.hostid ? host : part;

        return {
          gameid: data.gameid,
          hash: data.hash,
          value: data.value,
          min_val: 0,
          max_val: 0,
          is_pure: !!data.is_pure,
          host,
          part,
          winner,
          status: data.gamestatus || data.status,
          leftSide: data.hostchoice === 1 ? host : part,
          rightSide: data.hostchoice === 1 ? part : host,
          rehosts: 0,
        };
      };
      return flexRenderComponent(TableButtonCellComponent, {
        inputs: {
          text: 'View',
        },
        outputs: {
          handleClick: () => {
            modalService.openModal('CoinflipModal', {
              coinflip: transformData(cell.row.original),
            });
          },
        },
      });
    },
    enableSorting: false,
  },
  {
    accessorFn: (row) => row.opponentname,
    id: 'opponentname',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Opponent',
      };
    },
    cell: (cell) => {
      const cf = cell.row.original;
      return flexRenderComponent(PlayerRowComponent, {
        inputs: {
          player: {
            name: cf.opponentname,
            avatar: cf.opponentavatar,
            level: cf.opponentlevel,
            steamid: cf.partid,
          },
        },
      });
    },
  },
  {
    accessorFn: (row) => row.winner,
    id: 'winner',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Result',
      };
    },
    cell: (cell) => {
      const isWon = cell.cell.getValue() as boolean;
      const cf = cell.row.original;
      const isCancelled = cf.gamestatus === 'cancelled';
      return flexRenderComponent(TableColorCellComponent, {
        inputs: {
          value: isCancelled ? 'Cancelled' : isWon ? 'Won' : 'Lost',
          color: isCancelled
            ? 'var(--gray)'
            : isWon
            ? 'var(--green)'
            : 'var(--red)',
        },
      });
    },
  },
  {
    accessorFn: (row) => row.userchoice,
    id: 'userchoice',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Your Side',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(CoinflipCoinComponent, {
        inputs: {
          side: cell.getValue() as CoinflipCoin,
          size: '24px',
        },
      });
    },
  },
  {
    accessorFn: (row) => row.ended,
    id: 'ended',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Date',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(TableDateCellComponent, {
        inputs: {
          value: cell.getValue() as number,
        },
      });
    },
  },
  {
    accessorFn: (row) => row.status,
    id: 'status',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Status',
      };
    },
    cell: (cell) => {
      const cf = cell.row.original;

      const isResend =
        (cf.winner || cf.gamestatus === 'cancelled') &&
        STATUSES_ALLOW_REFUND.includes(cf.status);

      if (isResend) {
        return flexRenderComponent(TableButtonCellComponent, {
          inputs: {
            text: 'Resend',
          },
          outputs: {
            handleClick: () =>
              userHistoryService.resendTrade(cf.gameid, -1, 'coinflip'),
          },
        });
      } else {
        const getStatusText = () => {
          if (cf.winner || cf.gamestatus === 'cancelled') {
            if (cf.status === 'rehosted') {
              return {
                text: 'Double down or Rehosted',
                color: 'var(--green)',
              };
            }
            if (
              cf.status === 'accepted' ||
              cf.status === 'active' ||
              cf.status === 'sent'
            ) {
              return {
                text: capitalizeFirstLetter(cf.status),
                color: 'var(--green)',
              };
            }

            if (cf.status === 'refunded') {
              return {
                text: 'Refunded',
                color: 'var(--yellow)',
              };
            }

            if (STATUSES_ERROR.includes(cf.status)) {
              return {
                text: `Error: ${cf.status}`,
                color: 'var(--red)',
              };
            }
          }

          return {
            text: 'No Winnings',
            color: 'var(--red)',
          };
        };

        const { text, color } = getStatusText();

        return flexRenderComponent(TableColorCellComponent, {
          inputs: {
            value: text,
            color: color,
          },
        });
      }
    },
  },
  {
    id: 'fairness',
    header: () => 'Fairness',
    cell: (cell) =>
      flexRenderComponent(FairnessButtonComponent, {
        inputs: {
          href: `/provably-fair/coinflip/${cell.row.original.gameid}`,
        },
        outputs: {
          handleClick: () => modalService.close(),
        },
      }),
    enableSorting: false,
  },
];
